<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        :class="[
            'slice slice-cta slice-cta--with-profile',
            {
                'slice-cta--with-profile--theme-malt': theme === 'malt',
                'slice-cta--with-profile--theme-strategy': theme === 'strategy',
                'slice-cta--with-profile--theme-ai': theme === 'ai',
            },
        ]"
        aria-label="call-to-action"
        data-testid="slice-cta-with-profile"
    >
        <div class="slice-content">
            <div class="slice-cta__panel">
                <div class="slice-cta__media">
                    <CardHand v-if="cardHandEffect" :ms-theme="theme === 'strategy'">
                        <PrismicImage :field="slice.primary.image" data-testid="slice-cta-with-profile-image" />
                    </CardHand>
                    <PrismicImage
                        v-else
                        :field="slice.primary.image"
                        class="slice-cta__image"
                        width="240"
                        height="237"
                        data-testid="slice-cta-with-profile-image"
                    />
                </div>
                <div class="slice-cta__content-block">
                    <div class="slice-cta__category" data-testid="slice-cta-with-profile-category">{{ slice.primary.category }}</div>
                    <PrismicRichText :field="slice.primary.title" class="slice-title" data-testid="slice-cta-with-profile-title" />
                    <PrismicRichText
                        v-if="slice.primary.description"
                        :field="slice.primary.description"
                        class="slice-description"
                        data-testid="slice-cta-with-profile-description"
                    />
                    <div v-if="cta" class="slice-cta__cta">
                        <VJoyButton
                            :href="$withLocalePrefix(cta.url)"
                            :variant="cta.variant"
                            :size="ctaSize"
                            data-component-source="cta"
                            data-testid="slice-cta-with-profile-button"
                        >
                            {{ cta.text }}
                        </VJoyButton>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {CtaSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import CardHand from '../../../components/CardHand.vue';

    type CtaWithProfileVariation = Extract<CtaSlice, {variation: 'ctaWithProfile'}>;

    const props = defineProps(getSliceComponentProps<CtaWithProfileVariation>());

    const section = useTemplateRef('section');

    const theme = computed(() => props.slice.primary.theme ?? 'strategy');
    const cardHandEffect = computed(() => props.slice.primary.cardHandEffect ?? true);

    type CtaWithVariant = {url: string; text: string; variant: 'primary' | 'secondary' | undefined};

    const cta = computed<CtaWithVariant | null>(() => {
        const link = props.slice.primary.cta;
        if (!isFilled.link(link)) {
            return null;
        }

        const filledLink = link as {url: string; text?: string; variant?: 'primary' | 'secondary'};
        return {
            url: filledLink.url,
            text: filledLink.text ?? '',
            variant: filledLink.variant ?? undefined,
        };
    });

    const ctaSize = computed(() => ((cta.value?.text.length ?? 0) > 24 ? 'small' : 'medium') as 'small' | 'medium');

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-cta {
        &--with-profile {
            .slice-title {
                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-secondary-400);
                }
            }

            .slice-description {
                max-width: 450px;
                margin: var(--joy-core-spacing-6) auto 0;

                @include mq.screen_md(min) {
                    max-width: none;
                    text-align: left;
                    display: flex;
                }
            }

            .slice-cta__panel {
                border-radius: var(--joy-core-radius-4);
                background-color: var(--joy-color-secondary-10);
                padding: var(--joy-core-spacing-8) var(--joy-core-spacing-5);

                @include mq.screen_md(min) {
                    text-align: left;
                    display: flex;
                    padding: var(--joy-core-spacing-12) 80px;
                }
            }

            .slice-cta__content-block {
                @include mq.screen_md(min) {
                    flex: 1 1 0%;
                    margin-left: 80px;
                }
            }

            .slice-cta__media {
                margin-bottom: var(--joy-core-spacing-6);
            }

            .slice-cta__image {
                flex: none;
                width: 170px;

                @include mq.screen_sm(min) {
                    width: 240px;
                }
            }

            .slice-cta__category {
                font-weight: var(--joy-font-weight-bold);
                color: var(--joy-color-secondary-90);
                margin-bottom: var(--joy-core-spacing-2);
            }

            .slice-cta__cta {
                margin-top: var(--joy-core-spacing-6);

                .joy-button--slot {
                    white-space: wrap;
                }
            }

            &.slice-cta--with-profile--theme-malt {
                .slice-cta__panel {
                    background-color: var(--joy-color-primary-10);
                }

                .slice-cta__category {
                    color: var(--joy-color-primary-90);
                }
            }

            &.slice-cta--with-profile--theme-ai {
                .slice-cta__panel {
                    background-color: var(--joy-color-neutral-20);
                    flex-direction: row-reverse;
                }

                .slice-cta__category {
                    color: var(--joy-color-ai-primary-30);
                }

                .slice-cta__content-block {
                    @include mq.screen_md(min) {
                        margin-left: 0;
                        padding-right: 160px;
                    }
                }

                .slice-cta__content-block,
                .slice-cta__media {
                    flex: 1;
                }

                .slice-cta__media {
                    @include mq.screen_md(min) {
                        flex: none;
                        width: 50%;
                    }
                }

                .slice-cta__image {
                    width: 100%;
                    height: auto;
                    max-width: 550px;

                    @include mq.screen_md(min) {
                        max-width: none;
                    }
                }
            }
        }
    }
</style>
